import { createFileRoute } from "@tanstack/react-router";
import { getInventoryReport } from "@/lib/admin/analytics/reports.functions";
import { Card, DataTable, Kpi, KpiGrid, ReportPage, fmt, inr, useReport } from "@/components/admin/analytics/ReportKit";

export const Route = createFileRoute("/admin/analytics_/inventory")({
  head: () => ({ meta: [{ title: "Inventory Reports — CB16 Admin" }, { name: "robots", content: "noindex" }] }),
  component: InventoryReportsPage,
});

function InventoryReportsPage() {
  const report = useReport("inventory", getInventoryReport);
  return (
    <ReportPage eyebrow="Analytics" title="Inventory Reports" description="Current stock by size, plus movements in the selected period." report={report}>
      {(d, r) => {
        const file = `inventory-${r.range.days[0]}_${r.range.days[r.range.days.length - 1]}`;
        const k = d.kpis;
        const low = d.stock.filter((s) => s.low === "Yes");
        return (
          <>
            <KpiGrid>
              <Kpi label="Stock lines" value={fmt(k.skus)} sub="Product sizes tracked" />
              <Kpi label="Units on hand" value={fmt(k.unitsOnHand)} sub="Current" />
              <Kpi label="Units available" value={fmt(k.unitsAvailable)} sub={`${fmt(k.unitsReserved)} held for checkouts`} />
              <Kpi label="Stock value" value={inr(k.stockValue)} sub="At current selling price" />
              <Kpi label="Low stock" value={fmt(k.lowStock)} sub="At or below threshold" />
              <Kpi label="Units sold" value={fmt(k.unitsSold)} sub="Completed Live orders, this period" />
            </KpiGrid>
            <section className="grid gap-4 lg:grid-cols-2">
              <Card title="Low-stock items" rows={low} file={`${file}-low-stock`}>
                <DataTable head={["Product", "Size", "Available", "Threshold"]} rows={low.map((s) => [s.product, s.size, fmt(s.available), fmt(s.lowThreshold)])} empty="No items are at or below their low-stock threshold." />
              </Card>
              <Card title="Units sold (this period)" rows={d.sold} file={`${file}-sold`}>
                <DataTable head={["Product", "Units"]} rows={d.sold.map((s) => [s.product, fmt(s.units)])} />
              </Card>
            </section>
            <section className="grid gap-4 lg:grid-cols-2">
              <Card title="Checkout stock holds (this period)" rows={d.holds} file={`${file}-holds`}>
                <DataTable head={["Status", "Units"]} rows={d.holds.map((h) => [h.status, fmt(h.units)])} />
              </Card>
              <Card title="Purchases and stock usage">
                <p className="text-sm text-black/50">No data recorded. CB16 does not record stock purchases, purchase costs or stock usage, so these figures cannot be shown.</p>
              </Card>
            </section>
            <Card title="Stock levels (current)" rows={d.stock} file={`${file}-stock`}>
              <DataTable
                head={["Product", "Size", "Active", "On hand", "Held", "Available", "Low", "Unit price", "Value"]}
                rows={d.stock.map((s) => [s.product, s.size, s.active, fmt(s.onHand), fmt(s.reserved), fmt(s.available), s.low, inr(s.unitPrice), inr(s.stockValue)])}
              />
            </Card>
            <p className="text-[11px] text-black/40">Stock levels are today's figures; the date filter applies to units sold and checkout holds. Earlier test orders reduced stock as agreed.</p>
          </>
        );
      }}
    </ReportPage>
  );
}
